import React, { useState, useEffect } from 'react'
import { useOwner } from '../../contexts/OwnerContext'
import Page from '../../components/Page'
import { Brain } from 'lucide-react'
import PeriodFilter from '../../components/PeriodFilter'
import AIInsightsDisplay from '../../pages/shared-components/AIInsightsDisplay'
import { useGetCTEAIAnalysisQuery, MonthRange } from '../../services/api'
import { AIAssistantButton } from '../../components/AIAssistant'

const CTEsAnalysisPage: React.FC = () => {
  // Owner context
  const { isOwnerValid, isLoading: ownerLoading } = useOwner()

  // Estados do filtro de período
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())
  const [currentMonths, setCurrentMonths] = useState<number[]>([])
  const [hasSearched, setHasSearched] = useState(false)

  // Função para calcular filtro padrão: mês atual (sem mês selecionado → mês corrente)
  const getDefaultFilter = () => {
    const now = new Date()
    const currentMonth = now.getMonth() + 1 // getMonth() retorna 0-11
    return {
      year: now.getFullYear(),
      monthRanges: [{ startMonth: currentMonth, endMonth: currentMonth }] as MonthRange[]
    }
  }

  // Carregar dados do cache ao montar o componente
  useEffect(() => {
    if (isOwnerValid && !ownerLoading) {
      try {
        const cacheKey = 'cte_ai_analysis_cache'
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const cacheData = JSON.parse(cached)
          // Pegar o último item válido do cache
          const entries = Object.entries(cacheData)
          if (entries.length > 0) {
            const [_, data] = entries[entries.length - 1] as [string, any]
            if (data && data.filters) {
              setCurrentYear(data.filters.year)
              setCurrentMonths(
                data.filters.monthRanges?.flatMap((range: any) => {
                  const months: number[] = []
                  for (let m = range.startMonth; m <= range.endMonth; m++) {
                    months.push(m)
                  }
                  return months
                }) || []
              )
              setHasSearched(true)
            }
          }
        }
      } catch (error) {
        // Fail silently
      }
    }
  }, [isOwnerValid, ownerLoading])

  // Inicializar com filtro padrão se não houver cache (igual ao dashboard)
  useEffect(() => {
    if (isOwnerValid && !ownerLoading && currentMonths.length === 0 && !hasSearched) {
      const defaultFilter = getDefaultFilter()
      const monthsList: number[] = []
      defaultFilter.monthRanges.forEach(range => {
        for (let month = range.startMonth; month <= range.endMonth; month++) {
          if (!monthsList.includes(month)) {
            monthsList.push(month)
          }
        }
      })
      setCurrentYear(defaultFilter.year)
      setCurrentMonths(monthsList)
    }
  }, [isOwnerValid, ownerLoading, currentMonths.length, hasSearched])

  // RTK Query hook with cache - similar to dashboard pattern
  const queryParams = currentMonths.length > 0 ? { year: currentYear, months: currentMonths } : undefined
  const shouldSkip = !isOwnerValid || ownerLoading || currentMonths.length === 0

  // DEBUG: Log dos parâmetros da query
  console.log('[CTE Analysis] Query params:', {
    queryParams,
    shouldSkip,
    isOwnerValid,
    ownerLoading,
    currentMonths,
    currentYear
  })

  const {
    data: analysisResponse,
    isLoading: loading,
    error: queryError,
    refetch,
    isFetching
  } = useGetCTEAIAnalysisQuery(
    queryParams,
    {
      skip: shouldSkip,
      // Cache é gerenciado automaticamente por RTK Query
      pollingInterval: 0,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  )

  // DEBUG: Log da resposta e erro
  console.log('[CTE Analysis] Query response:', {
    analysisResponse,
    loading,
    isFetching,
    queryError,
    hasData: !!analysisResponse?.data
  })

  // DEBUG: Log detalhado do erro
  if (queryError) {
    console.error('[CTE Analysis] Error details:', {
      error: queryError,
      status: (queryError as any)?.status,
      data: (queryError as any)?.data,
      message: (queryError as any)?.message || (queryError as any)?.error
    })
  }

  const aiAnalysisData = (analysisResponse?.data as any) || null
  const error = queryError ? 'Erro ao carregar dados de análise de CTEs' : null

  // DEBUG: Log dos dados processados
  console.log('[CTE Analysis] Processed data:', {
    aiAnalysisData,
    error,
    hasSearched
  })

  const handlePeriodFilter = (year: number, monthRanges: MonthRange[]) => {
    console.log('[CTE Analysis] handlePeriodFilter called:', { year, monthRanges })

    // Atualizar os parâmetros irá automaticamente disparar nova query ou usar cache
    setCurrentYear(year)

    // Converter MonthRange[] para number[] (expandir ranges)
    const monthsList: number[] = []
    monthRanges.forEach(range => {
      for (let month = range.startMonth; month <= range.endMonth; month++) {
        if (!monthsList.includes(month)) {
          monthsList.push(month)
        }
      }
    })

    setCurrentMonths(monthsList)
    setHasSearched(true)

    // Salvar os filtros no localStorage para próximas visitas
    try {
      const cacheKey = 'cte_ai_analysis_cache'
      const cached = localStorage.getItem(cacheKey)
      const cacheData = cached ? JSON.parse(cached) : {}

      const keyForCache = `${year}_${monthsList.sort().join('_')}`
      cacheData[keyForCache] = {
        filters: { year, monthRanges },
        timestamp: Date.now()
      }

      localStorage.setItem(cacheKey, JSON.stringify(cacheData))
    } catch (error) {
      // Fail silently
    }
  }

  // Loading state (similar to dashboard)
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center transition-colors duration-300"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        <div className="text-center">
          <div className="relative">
            <div
              className="w-20 h-20 border-4 rounded-full animate-spin"
              style={{
                borderColor: 'var(--border-primary)',
                borderTopColor: '#3b82f6'
              }}
            ></div>
            <div
              className="absolute inset-0 w-20 h-20 border-4 border-transparent rounded-full animate-spin"
              style={{
                borderTopColor: '#1d4ed8',
                animationDirection: 'reverse',
                animationDuration: '1.5s'
              }}
            ></div>
          </div>
          <h2
            className="text-2xl font-bold mt-8 mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            Carregando Análise de CTEs
          </h2>
          <p
            style={{ color: 'var(--text-secondary)' }}
          >
            Processando dados e gerando insights com IA...
          </p>
        </div>
      </div>
    )
  }

  return (
    <Page
      title="🚛 Insights Inteligentes de CTEs"
      subtitle="Análise avançada com IA e filtros por período"
      onRefresh={() => {
        // Força refetch dos dados com os parâmetros atuais
        refetch()
      }}
      refreshing={isFetching}
    >
      <div className="space-y-6">
        {/* Filtro de Período */}
        <PeriodFilter
          onFilter={handlePeriodFilter}
          isLoading={isFetching}
        />

        {/* AI Assistant Button - Show when data is loaded */}
        {hasSearched && aiAnalysisData && (() => {
          // Contexto normalizado para CTEs
          const screenContext = 'CTEs'

          return <AIAssistantButton screenContext={screenContext} />
        })()}

        {/* Exibição de Insights de IA */}
        <AIInsightsDisplay
          data={aiAnalysisData}
          type="cte"
          isLoading={isFetching}
          error={error}
          hasSearched={hasSearched}
        />

        {/* Placeholder quando não há busca */}
        {!hasSearched && !isFetching && (
          <div className="text-center py-16">
            <div className="flex flex-col items-center space-y-4">
              <div className="p-6 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Brain className="h-12 w-12 text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  🤖 Análise Inteligente de CTEs
                </h3>
                <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  Selecione um período e clique em "Filtrar" para nossa IA analisar os dados de CTEs da sua empresa e gerar insights estratégicos
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  )
}

export default CTEsAnalysisPage
